import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import "./calendar.css";
import { useTabletOrMobile } from "../../hooks/responsive";
import { createPortal } from "react-dom";
import { dateRangeFormattingShortYear } from "../../utils/utils.js";
import { useTranslation } from "react-i18next";

const MobileCalendar = ({
  showCalendar,
  setShowCalendar,
  range,
  handleCalendarSave,
  numMonths,
  handleLoadMore,
  formatDate,
  autoDuration,
  disabledDates,
  defaultMonthDate,
  groupSize,
  setGroupSize,
  headerLabel,
}) => {
  const { t } = useTranslation();
  const [localRange, setLocalRange] = useState(range);

  const handleSave = () => {
    handleCalendarSave(localRange);
  };

  const handleClear = () => {
    setLocalRange({ from: undefined, to: undefined });
  };

  const handleSelect = (range, triggerDate) => {
    if (autoDuration && triggerDate) {
      const days = parseInt(autoDuration.split("D")[0]);
      const from = new Date(triggerDate);
      const to = new Date(triggerDate);
      to.setDate(to.getDate() + days - 1);
      setLocalRange({ from, to });
    } else {
      setLocalRange(range);
    }
  };

  return createPortal(
    <AnimatePresence>
      {showCalendar && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "tween" }}
          className="tw-fixed tw-inset-x-0 tw-bottom-0 tw-top-[16px] tw-z-[2000] tw-rounded-t-2xl tw-bg-white tw-shadow-xl"
        >
          <div className="tw-flex tw-h-full tw-flex-col">
            {/* Header */}
            <div className="tw-border-b tw-p-4">
              <div className="tw-flex tw-items-center tw-justify-between">
                <button onClick={() => setShowCalendar(false)} className="tw-text-gray-500">
                  ✕
                </button>
              </div>
              <div className="tw-mt-4">
                <h2 className="tw-text-l tw-font-semibold">
                  {localRange.to
                    ? `${t("selected", {
                        defaultValue: "Selected",
                      })}: ${dateRangeFormattingShortYear(localRange.from, localRange.to)}`
                    : headerLabel ||
                      t("select_trip_start_date", {
                        defaultValue: "Select trip start date",
                      })}
                </h2>
              </div>
              <div className="tw-mt-4 tw-flex">
                <div className="tw-w-[calc((100%-20px)/7)] tw-text-center tw-text-sm">Su</div>
                <div className="tw-w-[calc((100%-20px)/7)] tw-text-center tw-text-sm">Mo</div>
                <div className="tw-w-[calc((100%-20px)/7)] tw-text-center tw-text-sm">Tu</div>
                <div className="tw-w-[calc((100%-20px)/7)] tw-text-center tw-text-sm">We</div>
                <div className="tw-w-[calc((100%-20px)/7)] tw-text-center tw-text-sm">Th</div>
                <div className="tw-w-[calc((100%-20px)/7)] tw-text-center tw-text-sm">Fr</div>
                <div className="tw-w-[calc((100%-20px)/7)] tw-text-center tw-text-sm">Sa</div>
              </div>
            </div>

            {/* Calendar */}
            <div className="tw-flex tw-w-full tw-flex-1 tw-justify-center tw-overflow-auto tw-px-10 tw-py-4">
              <DayPicker
                mode="range"
                selected={localRange}
                onSelect={handleSelect}
                numberOfMonths={numMonths}
                pagedNavigation
                hideWeekdays
                disabled={disabledDates}
                defaultMonth={defaultMonthDate}
                hideNavigation={true}
                modifiersStyles={{
                  disabled: {
                    textDecoration: "line-through",
                    color: "#9ca3af", // Tailwind's text-gray-500 equivalent
                    pointerEvents: "none", // Optional: Make disabled days unclickable
                  },
                }}
                className="tw-w-full tw-max-w-full"
              />
            </div>

            {/* Footer */}
            <div className="tw-border-t tw-p-4">
              <div className="tw-flex tw-items-center tw-justify-between">
                {/* <div className="tw-flex tw-flex-col">
                  <span className="tw-mb-1 tw-text-xs tw-text-gray-700">
                    {t("estimated_group_size")}
                  </span>
                  <div className="tw-flex tw-items-center tw-overflow-hidden tw-rounded-lg">
                    <button
                      className="tw-bg-gray-100 tw-px-2 tw-py-1 tw-transition-colors hover:tw-bg-gray-200"
                      onClick={() => setGroupSize(Math.max(1, groupSize - 1))}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </button>
                    <span className="tw-px-4 tw-py-1">{groupSize}</span>
                    <button
                      className="tw-bg-gray-100 tw-px-2 tw-py-1 tw-transition-colors hover:tw-bg-gray-200"
                      onClick={() => setGroupSize(groupSize + 1)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </button>
                  </div>
                </div> */}
                <div className="tw-flex tw-flex-col"></div>
                <div className="tw-flex">
                  <button
                    onClick={handleSave}
                    disabled={!localRange.from || !localRange.to}
                    className={`tw-rounded-lg tw-px-6 tw-py-2 tw-font-bold tw-text-white tw-transition-colors ${
                      localRange.from && localRange.to
                        ? "tw-bg-[deepskyblue] hover:tw-bg-[#00a0e6]"
                        : "tw-cursor-not-allowed tw-bg-gray-300"
                    }`}
                  >
                    {t("select_dates", {
                      defaultValue: "Select dates",
                    })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const DesktopCalendar = ({
  showCalendar,
  setShowCalendar,
  range,
  handleCalendarSave,
  formatDate,
  autoDuration,
  disabledDates,
  defaultMonthDate,
  groupSize,
  setGroupSize,
  headerLabel,
}) => {
  const { t } = useTranslation();
  const [localRange, setLocalRange] = useState(range);

  const handleSave = () => {
    handleCalendarSave(localRange);
  };

  const handleClear = () => {
    setLocalRange({ from: undefined, to: undefined });
  };

  const handleSelect = (range, triggerDate) => {
    if (autoDuration && triggerDate) {
      const days = parseInt(autoDuration.split("D")[0]);
      const from = new Date(triggerDate);
      const to = new Date(triggerDate);
      to.setDate(to.getDate() + days - 1);
      setLocalRange({ from, to });
    } else {
      setLocalRange(range);
    }
  };

  return showCalendar ? (
    <div className="calendar-container tw-fixed tw-left-1/2 tw-top-1/2 tw-z-[9999] tw-h-[30rem] tw-min-w-[700px] -tw-translate-x-1/2 -tw-translate-y-1/2 tw-transform tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-2xl tw-transition-shadow tw-duration-300">
      <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between">
        <div className="tw-text-gray-700">
          {localRange.from && localRange.to ? (
            <>
              <div style={{ fontSize: "16px", color: "black" }}>
                {t("selected", { defaultValue: "Selected" })}:{" "}
                {dateRangeFormattingShortYear(localRange.from, localRange.to)}
              </div>
            </>
          ) : (
            <div className="tw-text-gray-500">
              {headerLabel ||
                t("select_trip_start_date", {
                  defaultValue: "Select trip start date",
                })}
            </div>
          )}
        </div>
      </div>

      <DayPicker
        mode="range"
        selected={localRange}
        onSelect={handleSelect}
        numberOfMonths={2}
        pagedNavigation
        disabled={disabledDates}
        defaultMonth={defaultMonthDate}
        modifiersStyles={{
          disabled: {
            textDecoration: "line-through",
            color: "#9ca3af", // Tailwind's text-gray-500 equivalent
            pointerEvents: "none", // Optional: Make disabled days unclickable
          },
        }}
      />

      <div className="tw-absolute tw-bottom-8 tw-left-8 tw-right-8 tw-flex tw-justify-between">
        {/* <div className="tw-flex tw-flex-col">
          <span className="tw-mb-1 tw-text-sm tw-text-gray-700">
            {t("estimated_group_size")}
          </span>
          <div className="tw-flex tw-items-center tw-overflow-hidden tw-rounded-lg">
            <button
              className="tw-bg-gray-100 tw-px-2 tw-py-1 tw-transition-colors hover:tw-bg-gray-200"
              onClick={() => setGroupSize(Math.max(0, groupSize - 1))}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
            <span className="tw-px-4 tw-py-1">{groupSize}</span>
            <button
              className="tw-bg-gray-100 tw-px-2 tw-py-1 tw-transition-colors hover:tw-bg-gray-200"
              onClick={() => setGroupSize(groupSize + 1)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
          </div>
        </div> */}

        <div className="tw-flex tw-flex-col"></div>
        <div className="tw-flex tw-gap-3">
          <button
            onClick={handleClear}
            className="tw-px-4 tw-py-2 tw-text-gray-600 tw-underline tw-transition-colors hover:tw-text-gray-400"
          >
            {t("clear", { defaultValue: "Clear" })}
          </button>
          <button
            onClick={handleSave}
            disabled={!localRange.from || !localRange.to}
            className={`tw-rounded-[8px] tw-px-4 tw-py-2 tw-text-white tw-transition-colors ${
              localRange.from && localRange.to
                ? "tw-bg-[deepskyblue] hover:tw-bg-[#00a0e6]"
                : "tw-cursor-not-allowed tw-bg-gray-300"
            }`}
          >
            {t("select_dates", { defaultValue: "Select dates" })}
          </button>
        </div>
      </div>
    </div>
  ) : null;
};

export default function Calendar({
  range,
  handleCalendarSave,
  showCalendar,
  setShowCalendar,
  autoDuration,
  disabledDates = [{ before: new Date() }],
  defaultMonthDate = new Date(),
  groupSize,
  setGroupSize,
  headerLabel,
}) {
  const [numMonths, setNumMonths] = useState(24);

  const isMobile = useTabletOrMobile();

  const formatDate = (date) => {
    if (!date) return "";

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleLoadMore = () => {
    setNumMonths((prev) => prev + 2);
  };

  // Add click event listener to handle clicks outside calendar
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!isMobile && showCalendar && !e.target.closest(".calendar-container")) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, showCalendar, setShowCalendar]);

  return (
    <>
      {isMobile ? (
        <MobileCalendar
          showCalendar={showCalendar}
          setShowCalendar={setShowCalendar}
          range={range}
          handleCalendarSave={handleCalendarSave}
          numMonths={numMonths}
          handleLoadMore={handleLoadMore}
          formatDate={formatDate}
          autoDuration={autoDuration}
          disabledDates={disabledDates}
          defaultMonthDate={defaultMonthDate}
          groupSize={groupSize}
          setGroupSize={setGroupSize}
          headerLabel={headerLabel}
        />
      ) : (
        <DesktopCalendar
          showCalendar={showCalendar}
          setShowCalendar={setShowCalendar}
          range={range}
          handleCalendarSave={handleCalendarSave}
          formatDate={formatDate}
          autoDuration={autoDuration}
          disabledDates={disabledDates}
          defaultMonthDate={defaultMonthDate}
          groupSize={groupSize}
          setGroupSize={setGroupSize}
          headerLabel={headerLabel}
        />
      )}
    </>
  );
}
